import React, { useState } from 'react';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { AdminUser, CreateAdminUserDto, UpdateAdminUserDto } from '../../types/admin';
import { Plus, Pencil, Trash2, UserCircle } from 'lucide-react';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  inactive: 'bg-slate-500/10 text-slate-400 border border-slate-500/20',
  locked: 'bg-rose-500/10 text-rose-400 border border-rose-500/20',
};

export const UsersPage: React.FC = () => {
  const { users, roles, departments, isLoading, createUser, updateUser, deleteUser } = useAdminConfig();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<CreateAdminUserDto>({
    email: '', password: '', name: '', role_id: '', department_id: '', phone: '', status: 'active',
  });
  const [saving, setSaving] = useState(false);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleName = (roleId: string) => roles.find(r => r.role_id === roleId)?.name || roleId;

  const openCreate = () => {
    setEditing(null);
    setForm({ email: '', password: '', name: '', role_id: '', department_id: '', phone: '', status: 'active' });
    setFormOpen(true);
  };

  const openEdit = (user: AdminUser) => {
    setEditing(user);
    setForm({
      email: user.email,
      password: '',
      name: user.name,
      role_id: user.role_id,
      department_id: user.department_id || '',
      phone: user.phone || '',
      status: user.status,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const dto: UpdateAdminUserDto = {
          name: form.name,
          role_id: form.role_id,
          department_id: form.department_id || undefined,
          phone: form.phone || undefined,
          status: form.status,
        };
        await updateUser(editing.user_id, dto);
      } else {
        await createUser(form as CreateAdminUserDto);
      }
      setFormOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <LoadingSpinner size="lg" text="Loading Users..." />;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-slate-100">User Management</h3>
        <p className="text-xs text-slate-400">Create, edit, and manage system users</p>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="form-input w-64"
          />
          <Button onClick={openCreate} size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add User
          </Button>
        </div>

        {filtered.length === 0 ? (
          <p className="text-slate-400 text-sm py-8 text-center">No users found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50 text-left">
                  <th className="pb-2 font-medium text-slate-400">User</th>
                  <th className="pb-2 font-medium text-slate-400">Role</th>
                  <th className="pb-2 font-medium text-slate-400">Department</th>
                  <th className="pb-2 font-medium text-slate-400">Status</th>
                  <th className="pb-2 font-medium text-slate-400 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(user => (
                  <tr key={user.user_id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <UserCircle className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="font-semibold text-slate-100">{user.name}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 text-slate-300">{getRoleName(user.role_id)}</td>
                    <td className="py-3 text-slate-300">
                      {departments.find(d => d.department_id === user.department_id)?.name || '-'}
                    </td>
                    <td className="py-3">
                      <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLES[user.status] || STATUS_STYLES.inactive}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <button onClick={() => openEdit(user)} className="p-1.5 text-slate-400 hover:text-sky-400 rounded-lg" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteUser(user.user_id)} className="p-1.5 text-slate-400 hover:text-rose-400 rounded-lg ml-1" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Edit User' : 'Create User'}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Name</label>
            <input type="text" className="form-input w-full" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Email</label>
            <input type="email" className="form-input w-full" value={form.email} disabled={!!editing}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          {!editing && (
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Password</label>
              <input type="password" className="form-input w-full" value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Role</label>
            <select className="form-input w-full" value={form.role_id}
              onChange={e => setForm(f => ({ ...f, role_id: e.target.value }))}>
              <option value="">Select role</option>
              {roles.map(r => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Department</label>
            <select className="form-input w-full" value={form.department_id}
              onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
              <option value="">None</option>
              {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Phone</label>
            <input type="text" className="form-input w-full" value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select className="form-input w-full" value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value as 'active' | 'inactive' | 'locked' }))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="locked">Locked</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.name || !form.email || !form.role_id || (!editing && !form.password)}>
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
